/**
 * Lightweight toast helper — module-level subscribable store.
 *
 * Why not React Context: toast.success() needs to be callable from
 * mutations onSuccess (outside component tree), and Context would force
 * a hook in every consumer. Module singleton is simpler and zero overhead.
 *
 * API:
 *   toast.success(title, { description?, duration? })
 *   toast.error(title, { description?, duration? })
 *   toast.info(title, { description?, duration? })
 *   useToasts()  -> subscriber hook for the renderer (Toast.tsx)
 *
 * Toast lifecycle:
 *   1. toast.X() called -> push to queue with auto-incremented id
 *   2. Subscribers (useToasts) re-render with updated list
 *   3. After duration ms (default 4000), auto-dismiss
 *   4. User can also dismiss manually via X button (Toast.tsx)
 */

import { useSyncExternalStore } from "react";

export type ToastTone = "success" | "error" | "info";

export type ToastItem = {
  id: number;
  tone: ToastTone;
  title: string;
  description?: string;
  duration: number;
  createdAt: number;
};

type ToastOptions = {
  description?: string;
  /** Duration in ms; 0 means no auto-dismiss. Default: 4000. */
  duration?: number;
};

// --- Singleton store on globalThis ---
//
// CRITICAL: this store MUST be a true singleton. Vite's manualChunks splits
// only vendor libs; this local module gets inlined into EVERY chunk that
// imports it (observed: 3 copies across index/tanstack/xlsx chunks). With
// per-module state, toast.success() writes to one copy's queue while the
// ToastViewport's useToasts() reads another copy's queue -> toasts never
// render. Hanging the state off globalThis guarantees one shared store no
// matter how many times the module is duplicated by bundling.

type ToastStore = {
  nextId: number;
  items: ToastItem[];
  listeners: Set<() => void>;
};

const store: ToastStore = ((globalThis as Record<string, unknown>).__sendsmsToastStore as ToastStore) ?? {
  nextId: 1,
  items: [],
  listeners: new Set<() => void>(),
};
(globalThis as Record<string, unknown>).__sendsmsToastStore = store;

function emit() {
  for (const l of store.listeners) l();
}

function push(tone: ToastTone, title: string, opts: ToastOptions = {}) {
  const id = store.nextId++;
  const duration = opts.duration ?? 4000;
  const item: ToastItem = {
    id,
    tone,
    title,
    description: opts.description,
    duration,
    createdAt: Date.now(),
  };
  store.items = [...store.items, item];
  emit();
  if (duration > 0) {
    setTimeout(() => dismiss(id), duration);
  }
  return id;
}

export function dismiss(id: number) {
  const before = store.items.length;
  store.items = store.items.filter((it) => it.id !== id);
  if (store.items.length !== before) emit();
}

export function dismissAll() {
  if (store.items.length === 0) return;
  store.items = [];
  emit();
}

// --- Public API ---

export const toast = {
  success: (title: string, opts?: ToastOptions) => push("success", title, opts),
  error: (title: string, opts?: ToastOptions) => push("error", title, opts),
  info: (title: string, opts?: ToastOptions) => push("info", title, opts),
};

// --- Subscriber hook (for Toast.tsx renderer) ---

function subscribe(cb: () => void): () => void {
  store.listeners.add(cb);
  return () => store.listeners.delete(cb);
}

function getSnapshot(): ToastItem[] {
  return store.items;
}

export function useToasts(): ToastItem[] {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}
